import React, { useRef, useState } from 'react';
import { ChevronLeft, Camera } from 'lucide-react';
import Button from '../ui/Button';
import { useStudentForm } from '../../hooks/useStudentForm';
import { validateStudentForm, showValidationErrorsWithToast } from '../../utils/formValidators';
import studentService from '../../services/studentService';
import { useToast } from '../../hooks/useToast';

/**
 * 新增学生表单
 */
const StudentFormCreate = ({ onBack, onSave, isSaving = false }) => {
  const {
    genderOptions,
    statusOptions,
  } = useStudentForm();

  const toast = useToast();

  // 表单字段引用
  const nameRef = useRef(null);
  const genderRef = useRef(null);
  const birthYearRef = useRef(null);
  const birthMonthRef = useRef(null);
  const gradeRef = useRef(null);
  const phoneRef = useRef(null);
  const referrerRef = useRef(null);
  const statusRef = useRef(null);
  const noteRef = useRef(null);
  // 用户账号字段引用
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  // 年级状态（只读显示）
  const [calculatedGrade, setCalculatedGrade] = useState('');
  // 用户名默认值（跟随姓名）
  const [defaultUsername, setDefaultUsername] = useState('');
  // 防抖计时器引用
  const debounceTimerRef = useRef(null);

  // 姓名变化时更新默认用户名
  const handleNameChange = () => {
    const name = nameRef.current?.value || '';
    setDefaultUsername(name);
    // 如果用户名字段未手动修改，则同步更新
    if (usernameRef.current && !usernameRef.current.dataset.modified) {
      usernameRef.current.value = name;
    }
  };

  // 标记用户名字段已被手动修改
  const handleUsernameChange = () => {
    if (usernameRef.current) {
      usernameRef.current.dataset.modified = 'true';
    }
  };

  // 自动计算年级 (防抖处理)
  const handleBirthDateChange = () => {
    // 清除之前的计时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const birthYear = birthYearRef.current?.value;
    const birthMonth = birthMonthRef.current?.value;
    
    if (!birthYear) {
      setCalculatedGrade('');
      return;
    }

    const currentYear = new Date().getFullYear();
    const age = currentYear - parseInt(birthYear);

    if (age >= 18) {
      setCalculatedGrade('');
      return;
    }

    // 设置 800ms 的防抖
    debounceTimerRef.current = setTimeout(async () => {
      try {
        console.log('正在计算年级:', { birthYear, birthMonth });
        const result = await studentService.calculateGrade({
          birth_year: parseInt(birthYear),
          birth_month: birthMonth ? parseInt(birthMonth) : undefined
        });
        setCalculatedGrade(result.grade);
      } catch (error) {
        console.error('计算年级失败:', error);
        // 不显示 toast，避免输入过程中的频繁干扰，只记录日志
      }
    }, 800);
  };
  // 处理保存
  const handleSave = () => {
    // 注意：防重入锁由父组件 App.jsx 的 isSavingRef 统一管理，此处不重复判断
    const formData = {
      name: nameRef.current?.value || '',
      gender: genderRef.current?.value || '',
      birth_year: birthYearRef.current?.value ? parseInt(birthYearRef.current.value) : null,
      birth_month: birthMonthRef.current?.value ? parseInt(birthMonthRef.current.value) : null,
      grade: gradeRef.current?.value || '',
      contact: phoneRef.current?.value || '',
      hobbies: referrerRef.current?.value || '',  // 推荐人临时映射到 hobbies
      student_type: statusRef.current?.value || '在学',
      remarks: noteRef.current?.value || '',
      // 用户账号字段
      username: usernameRef.current?.value || nameRef.current?.value || '',
      password: passwordRef.current?.value || '',
    };

    // 使用验证工具验证
    const validation = validateStudentForm(formData);
    if (!validation.valid) {
      showValidationErrorsWithToast(validation.errors, toast);
      return;
    }

    onSave(formData);
  };

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      <header className="h-[56px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 -ml-1 text-[#2D3748]">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-[#2D3748]">新增学生</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pb-24 space-y-4">
        {/* 头像上传 */}
        <div className="flex flex-col items-center justify-center py-6 bg-white rounded-xl border border-[#EDF2F7]">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mb-2 border-2 border-dashed border-gray-300 bg-gray-100">
            <Camera size={24} className="text-gray-400" />
          </div>
          <span className="text-xs text-[#3182CE] font-medium">上传头像</span>
        </div>

        {/* 基本信息表单 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          {/* 学生姓名 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              学生姓名
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={nameRef}
              type="text" 
              placeholder="请输入姓名" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              onChange={handleNameChange}
              required
            />
          </div>

          {/* 性别 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              性别
              <span className="text-red-500">*</span>
            </label>
            <select 
              ref={genderRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
              required
            >
              <option value="">请选择性别</option>
              {genderOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* 出生年份 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              出生年份
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={birthYearRef}
              type="number" 
              placeholder="如: 2018" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              min="1900"
              max={new Date().getFullYear()}
              onChange={handleBirthDateChange}
              required
            />
          </div>

          {/* 出生月份 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              出生月份
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={birthMonthRef}
              type="number" 
              placeholder="1-12" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              min="1"
              max="12"
              onChange={handleBirthDateChange}
              required
            />
          </div>

          {/* 年级（自动计算） */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              年级
            </label>
            <input 
              ref={gradeRef}
              type="text" 
              value={calculatedGrade}
              placeholder="根据出生日期自动计算" 
              className="flex-1 text-sm text-right bg-gray-100 outline-none text-[#2D3748] cursor-not-allowed"
              readOnly
            />
          </div>

          {/* 联系方式 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568] flex items-center gap-1">
              联系方式
              <span className="text-red-500">*</span>
            </label>
            <input 
              ref={phoneRef}
              type="tel" 
              placeholder="请输入家长电话" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              required
            />
          </div>

          {/* 推荐人 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              推荐人
            </label>
            <input 
              ref={referrerRef}
              type="text" 
              placeholder="请输入推荐人姓名" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
            />
          </div>

          {/* 状态 */}
          <div className="flex items-center px-4 py-3">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              状态
            </label>
            <select 
              ref={statusRef}
              className="flex-1 text-sm bg-transparent outline-none text-[#2D3748] dir-rtl text-right"
              defaultValue="在学"
            >
              {statusOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        {/* 备注 */}
        <div>
          <label className="text-sm font-medium text-[#4A5568] mb-2 block px-1">备注</label>
          <textarea 
            ref={noteRef}
            className="w-full bg-white border border-[#EDF2F7] rounded-xl p-3 text-sm h-24 focus:outline-none focus:border-[#3182CE]" 
            placeholder="请输入学生备注信息..."
          />
        </div>

        {/* 登录账号设置 */}
        <div className="bg-white rounded-xl border border-[#EDF2F7] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#EDF2F7] bg-gray-50">
            <span className="text-sm font-medium text-[#4A5568]">登录账号设置</span>
          </div>
          
          {/* 用户名 */}
          <div className="flex items-center px-4 py-3 border-b border-[#EDF2F7]">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              用户名
            </label>
            <input 
              ref={usernameRef}
              type="text" 
              placeholder="默认与学生姓名相同" 
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
              onChange={handleUsernameChange}
            />
          </div>

          {/* 登录密码 */}
          <div className="flex items-center px-4 py-3">
            <label className="w-20 text-sm font-medium text-[#4A5568]">
              密码
            </label>
            <input 
              ref={passwordRef}
              type="text" 
              autoComplete="off"
              placeholder="留空则不开通登录账号（至少6位）"
              className="flex-1 text-sm text-right bg-transparent outline-none text-[#2D3748]"
            />
          </div>
        </div>
      </main>

      {/* 底部按钮区 - 新增模式 */}
      <div className="h-[80px] bg-white border-t border-[#EDF2F7] px-6 flex items-center justify-between flex-shrink-0 z-10">
        <Button variant="secondary" onClick={onBack} className="w-32" disabled={isSaving}>取消</Button>
        <Button variant="primary" onClick={handleSave} className="w-32" disabled={isSaving}>
          {isSaving ? '保存中...' : '保存'}
        </Button>
      </div>
    </div>
  );
};

export default StudentFormCreate;
